import { toNumber } from 'lodash'
import { useLayoutEffect, useRef, useState } from 'react'
import { DataWithKey } from '../../Component/EnhancedData/Types'
import * as EDT from '../../Type/EnhancedData'
import { LineItemRowErrorMap, LineItemStateData } from '../../Type/EnhancedData'
import { FieldMap } from '../../types'
import { initialProductItem } from './Constants'

export const makePersistentKey = (key: string, ...prefixes: string[]) => ([...prefixes, key].join('::'))

/**
 * Maps properties from a source object to a target object structure.
 * Skips mapping if the source value is null or undefined.
 * Automatically converts string values to numbers when the target field defaults to a numeric type.
 *
 * @param source - The incoming raw source object.
 * @param fieldMapping - Object mapping source keys to target keys.
 * @param defaultData - Optional target object template used for default values and runtime type detection.
 * @returns Formatted target object of type T.
 */
export const toTarget = <S extends Record<string, any>, T extends Record<string, any>>(
  source: S,
  fieldMapping: FieldMap<S, T>,
  defaultData?: T
): T => {
  const fallbackTarget = defaultData ? { ...defaultData } : ({} as T)

  return Object.entries(fieldMapping).reduce<T>(
    (acc, [sourceKey, targetKey]) => {
      const sourceValue = source[sourceKey as keyof S]
      const tKey = targetKey as keyof T

      // Early skip: do not process if the source value is null or undefined
      if (!sourceValue) return acc

      // Convert to number if target property is a numeric type
      const isTargetNumber = defaultData && typeof defaultData[tKey] === 'number'
      if (isTargetNumber) {
        acc[tKey] = toConsumableNumber(sourceValue, defaultData[tKey]) as T[keyof T]
      } else {
        acc[tKey] = sourceValue as unknown as T[keyof T]
      }
      return acc
    },
    fallbackTarget
  )
}

export const toSource = <S extends object, T extends object>(
  target: T,
  fieldMapping: FieldMap<S, T>,
  defaultData?: S
): S => {
  const fallbackSource = defaultData ? { ...defaultData } : ({} as S)

  return Object.entries(fieldMapping).reduce<S>(
    (acc, [sourceKey, targetKey]) => {
      acc[sourceKey as keyof S] = (target[targetKey as keyof T] as unknown) as S[keyof S]
      return acc
    },
    fallbackSource
  )
}

export const toDataWithKey = <T extends object>(data: T, uniqueKey?: string | number) => {
  return {
    key: uniqueKey || Math.random().toString(36).slice(2, 11),
    ...data,
  }
}

export const toTargetDataWithKey = <S extends Object, T extends Object>(
  source: S,
  fieldMapping: FieldMap<S, T>,
  defaultData?: T,
  uniqueKey?: string | number
): DataWithKey<T> => {
  return toDataWithKey(
    toTarget(source, fieldMapping, defaultData),
    uniqueKey,
  )
}

export const hasBackendError = (errorResponse: EDT.EnhancedDataBackendError): boolean => (
  (
    errorResponse.otherErrors.length > 0 ||
    Object.keys(errorResponse.lineItemErrors).length > 0 ||
    Object.keys(errorResponse.fieldErrorMap).length > 0
  )
)

export const hasValidationError = (validationError: EDT.EnhancedDataValidationError): boolean => (
  Object.keys(validationError.inputErrors).length > 0 ||
  Object.keys(validationError.lineItemErrors).length > 0
)

export const getNextIndex = (items?: Record<any, any>): number => {
  if (!items) return -1
  return Object.keys(items).length
}

export const isEmptyLineItem = (item: EDT.LineItemStateData): boolean => {
  const comparableKeys: Array<keyof EDT.LineItemStateData> = [
    'productName', 'quantity', 'unitOfMeasure', 'costPerUnit', 'itemTax',
  ]

  // Helper function to trim whitespace from string values
  const normalize = (val: unknown) => (typeof val === 'string' ? val.trim() : val)

  // Compare only the explicitly listed keys
  return comparableKeys.every((key) => {
    const itemValue = normalize(item[key])
    const defaultValue = normalize(initialProductItem[key])
    return itemValue === defaultValue
  })
}

export const sortLineItemRowErrorMap = (
  errorMap: LineItemRowErrorMap | undefined | null
): LineItemRowErrorMap => {
  if (!errorMap) return {}

  return Object.fromEntries(
    Object.entries(errorMap).sort(
      ([, errorA], [, errorB]) => errorA!.index - errorB!.index
    )
  )
}

export const updateLineItemIndices = (
  lineItemMap: Record<string, DataWithKey<LineItemStateData>>
): Record<string, DataWithKey<LineItemStateData>> => {
  console.debug('reconcile line item indices')
  const updatedEntries = Object.entries(lineItemMap).map(([key, lineItemData], newIndex) => {
    const updatedLineItem: DataWithKey<LineItemStateData> = {
      ...lineItemData,
      index: newIndex,
      rowNumber: (newIndex + 1),
    }
    return [key, updatedLineItem]
  })
  return Object.fromEntries(updatedEntries)
}

export const updateLineItemRowErrorIndices = (
  lineItemMap: Record<string, DataWithKey<LineItemStateData>>,
  errorMap: LineItemRowErrorMap,
): LineItemRowErrorMap => {
  const updatedErrorMap: LineItemRowErrorMap = {}

  for (const [key, rowError] of Object.entries(errorMap)) {
    if (!rowError) continue

    const matchingLineItem = lineItemMap[key]

    if (matchingLineItem) {
      updatedErrorMap[key] = {
        ...rowError,
        index: matchingLineItem.index,
        rowNumber: matchingLineItem.rowNumber,
      }
    }
  }
  return updatedErrorMap
}

export function useTableDynamicHeight<T> (
  isScrollable: boolean,
  scrollThreshold: number | undefined,
  rows: T[]
) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [dynamicHeight, setDynamicHeight] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    if (isScrollable && wrapperRef.current && scrollThreshold) {
      const thead = wrapperRef.current.querySelector('thead')
      const tbody = wrapperRef.current.querySelector('tbody')

      let totalHeight = 0

      if (thead) {
        totalHeight += thead.getBoundingClientRect().height
      }

      if (tbody) {
        const bodyRows = Array.from(tbody.children)
        for (let i = 0; i < scrollThreshold && i < bodyRows.length; i++) {
          totalHeight += bodyRows[i].getBoundingClientRect().height
        }
      }

      setDynamicHeight(totalHeight)
    }
  }, [isScrollable, rows, scrollThreshold])

  return { wrapperRef, dynamicHeight }
}

export const normalizeData = <T>(data: T): any => {
  if (data === null || data === undefined || (data as unknown) === '') {
    return ''
  }
  if (typeof data !== 'object') {
    return data
  }
  if (Array.isArray(data)) {
    return data.map(normalizeData)
  }

  return Object.keys(data)
    .sort()
    .reduce((acc: Record<string, any>, key) => {
      acc[key] = normalizeData((data as Record<string, any>)[key])
      return acc
    }, {})
}

export const serializeObject = <T>(data: T, normalize: boolean = false): string => {
  if (normalize) {
    const normalizedData: any = normalizeData(data)
    return JSON.stringify(normalizedData)
  }
  return JSON.stringify(data)
}

export function eligibleCardForEnhancedData (cardLevel: number, cardBrand: string): boolean {
  return Boolean(cardLevel > 2 && ['VI', 'VS', 'MC'].includes(cardBrand?.toUpperCase()))
}

export const toConsumableNumber = (value: any, fallback: number = 0): number => {
  const valNumber = toNumber(value)
  return !isNaN(valNumber) ? toNumber(valNumber.toFixed(2)) : fallback
}
